import React, { Component } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
  labelStyle,
  stroke,
  DownloadButton,
  tooltip,
  lineXaxis,
} from '../options';
import reducedData from '../../reducedData.json';
import trans from '../../translations.json';
import StackedTable from './percentPositiveByAge-Table';
import { ButtonGroup } from '../buttonGroup/buttonGroup';
import './tests.scss';
import { Checkbox } from 'antd';
import compareChartColours from '../compareChart/compareChart.scss';

const viewModes = {
  graph: 'graph',
  table: 'table',
};

const chartColours = [
  compareChartColours.compareChartColour1,
  compareChartColours.compareChartColour2,
  compareChartColours.compareChartColour3,
  compareChartColours.compareChartColour4,
  compareChartColours.compareChartColour5,
];

const ageData = Object.values(
  reducedData.dataPositiveByAge,
).reverse();

class PositiveByAge extends Component {
  state = {
    ageData: ageData,
    ready: false,
    series: [],
    options: {},
    selection: [
      ...Object.values(trans.testing.ageCategories[this.props.lang]),
    ],
    keys: [
      ...Object.values(trans.testing.ageCategories[this.props.lang]),
    ],
    selectedViewMode: viewModes.graph,
    selectedSeries: [],
    chartRef: React.createRef(null),
    selectionColors: chartColours,
  };

  onViewModeChange = (viewMode) => {
    this.setState({
      selectedViewMode: viewMode,
    });
  };

  componentDidMount() {
    this.setData();
  }

  prepData = () => {
    let byAgeData = [
      {
        name: Object.values(
          trans.testing.ageCategories[this.props.lang],
        )[0],
        data: [],
        type: 'line',
      },
      {
        name: Object.values(
          trans.testing.ageCategories[this.props.lang],
        )[1],
        data: [],
        type: 'line',
      },
      {
        name: Object.values(
          trans.testing.ageCategories[this.props.lang],
        )[2],
        data: [],
        type: 'line',
      },
      {
        name: Object.values(
          trans.testing.ageCategories[this.props.lang],
        )[3],
        data: [],
        type: 'line',
      },
      {
        name: Object.values(
          trans.testing.ageCategories[this.props.lang],
        )[4],
        data: [],
        type: 'line',
      },
    ];

    ageData.forEach((item, i) => {
      switch (item.age_category) {
        case '0to13':
          byAgeData[0].data.push([
            item.DATE,
            (item.percent_positive_7d_avg * 100).toFixed(2),
          ]);
          break;
        case '14to17':
          byAgeData[1].data.push([
            item.DATE,
            (item.percent_positive_7d_avg * 100).toFixed(2),
          ]);
          break;
        case '18to24':
          byAgeData[2].data.push([
            item.DATE,
            (item.percent_positive_7d_avg * 100).toFixed(2),
          ]);
          break;
        case '25to64':
          byAgeData[3].data.push([
            item.DATE,
            (item.percent_positive_7d_avg * 100).toFixed(2),
          ]);
          break;
        case '65+':
          byAgeData[4].data.push([
            item.DATE,
            (item.percent_positive_7d_avg * 100).toFixed(2),
          ]);
          break;
        default:
          break;
      }
    });

    byAgeData.filter((item) =>
      this.state.selection.includes(item.name),
    );

    return byAgeData;
  };

  toggleSelection = (name) => {
    const byAgeData = this.prepData();
    var selection = [...this.state.selection];
    //check if it's already selected
    var currentSelectionKey = selection.indexOf(name);
    var availColour = selection.indexOf(null);

    if (currentSelectionKey !== -1) {
      selection[currentSelectionKey] = null;
    } else if (availColour !== -1) {
      selection[availColour] = name;
    }

    const newSelectionColors = chartColours.filter(
      (color, ind) => selection[ind],
    );

    this.setState({
      selectionLimitReached:
        currentSelectionKey === -1 && availColour === -1,
      selection: selection,
      selectionColors: newSelectionColors,
      series: [
        ...byAgeData.filter((item) => selection.includes(item.name)),
      ],
    });
  };

  setData = () => {
    const byAgeData = this.prepData();

    let show = Boolean(
      this.state.selection.reduce((sum, sel) => sum + sel),
    );

    this.setState({
      series: [...byAgeData],
      options: {
        colors: this.state.selectionColors,
        markers: {
          size: 0,
          strokeColors: '#fff',
          strokeWidth: 1,
          hover: {
            size: 9,
          },
        },
        stroke: stroke,
        legend: {
          show: false,
        },
        tooltip: {
          ...tooltip,
          y: {
            formatter: function (value) {
              return value + '%';
            },
          },
        },
        chart: {
          animations: {
            enabled: false,
            animateGradually: {
              enabled: false,
            },
            dynamicAnimation: {
              enabled: false,
            },
          },
          type: 'line',
          toolbar: {
            show: true,
            tools: {
              download: DownloadButton(
                this.props.lang,
                this.props.chartTitle,
              ),
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          zoom: { enabled: true },
        },
        plotOptions: {
          bar: {
            horizontal: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        yaxis: {
          title: {
            text: trans.testing.yaxisPerc[this.props.lang],
          },
          decimalsInFloat: 0,
          labels: {
            style: { ...labelStyle },
            formatter: (value) =>
              this.props.lang === 'fr'
                ? value.toLocaleString()
                : value
                    .toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '%',
          },
        },
        xaxis: {
          ...lineXaxis(show),
        },
        responsive: [
          {
            breakpoint: 640,
            options: {
              chart: {
                animations: {
                  enabled: false,
                  animateGradually: {
                    enabled: false,
                  },
                  dynamicAnimation: {
                    enabled: false,
                  },
                },
                offsetX: 10,
                height: '200px',
                toolbar: {
                  tools: {
                    zoomin: false,
                    zoomout: false,
                  },
                },
              },
              xaxis: {
                labels: {
                  hideOverlappingLabels: true,
                },
              },
              legend: {
                fontSize: '10px',
              },
              dataLabels: {
                enabled: false,
              },
            },
          },
        ],
        // fill: {
        //   opacity: [0.2, 1],
        // },
      },
      ready: true,
    });
  };

  render() {
    const CheckboxesNames = () =>
      this.state.keys
        .filter((item) =>
          Object.values(
            trans.testing.ageCategories[this.props.lang],
          ).includes(item),
        )
        .map((item, key) => {
          var selectionKey = this.state.selection.includes(item)
            ? `compare-chart-${this.state.selection.indexOf(item)}`
            : '';

          return (
            <Checkbox
              checked={this.state.selection.includes(item)}
              onClick={() => this.toggleSelection(item)}
              className={`${selectionKey}`}
              key={key}
            >
              {
                Object.values(
                  trans.testing.ageCategories[this.props.lang],
                )[key]
              }
            </Checkbox>
          );
        });

    const GetRightView = () => {
      switch (this.state.selectedViewMode) {
        case viewModes.graph:
          return (
            <>
              <CheckboxesNames />
              <ReactApexChart
                ref={this.state.chartRef}
                options={this.state.options}
                series={this.state.series}
                type="line"
                height="400px"
                aria-hidden="true"
              />
            </>
          );
        case viewModes.table:
          return <StackedTable lang={this.props.lang} />;
        default:
          break;
      }
    };

    return (
      <div id="">
        {this.state.ready && (
          <>
            <p>
              {trans.testing.byAgesDescB[this.props.lang]}{' '}
              <a
                href="/data/covid-19-case-data-glossary"
                target="_blank"
                rel="noopener"
              >
                {trans.testing.byAgesDesc[this.props.lang]}
              </a>
              .
            </p>
            <ButtonGroup
              selected={this.state.selectedViewMode}
              viewModes={viewModes}
              lang={this.props.lang}
              handleOnClick={this.onViewModeChange}
            />
            <GetRightView />
          </>
        )}
      </div>
    );
  }
}
export default PositiveByAge;
